import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import type { CoverageType } from './insuranceCoverage'
import { LeadSource } from './salesLead'

export interface FunnelStage {
  label: string
  leads: number
  conversionFromPrevious: number
  conversionFromTop: number
}

export interface ClosedWonMonth {
  month: string
  /** Customers won; lower than `policies` when one buys several lines. */
  accounts: number
  policies: number
  premium: number
}

export interface ClosedWonTotals {
  /** Distinct customers in the range — never the sum of the monthly counts. */
  accounts: number
  policies: number
  premium: number
  /** Policies this axis cannot place, so they are absent from the months above. */
  undated: number
}

export interface ClosedWonRepRow {
  repId: string
  repName: string
  accounts: number
  policies: number
  premium: number
}

export interface ClosedWonDetailRow {
  policyId: string
  policyIdShort: string
  account: string
  orgId: string
  orgName: string
  repId: string
  repName: string
  vendor: string
  boundDate: string
  policyStart: string | null
  premium: number
}

/** Lines that are not one coverage type: one-time fees, and coverages saved with a type it doesn't know. */
export enum PremiumBucket {
  OTHER = 'OTHER',
  FEES = 'FEES',
}

export type PremiumLineType = CoverageType | PremiumBucket

export interface PremiumLine {
  /** The coverage or fee record's own id — two fees can share a label. */
  id: string
  lineType: PremiumLineType
  /** The coverage name, or the fee's own label — "Surplus Lines Tax" and "Taxes" differ. */
  lineLabel: string
  /** Null when the record carries no amount, which is not zero. */
  premium: number | null
}

/** A detail-table row with the coverage and fee lines its premium is made of. */
export interface ClosedWonBreakdownRow extends ClosedWonDetailRow {
  coverageCount: number
  /** Lines with no amount, so missing from `premium`. */
  unpricedLines: number
  lines: PremiumLine[]
}

export interface PremiumLineTotal {
  lineType: PremiumLineType
  label: string
  /** What the priced lines add up to. */
  premium: number
  /** Fraction of total premium, computed server-side like every other figure. */
  share: number
  lines: number
  /** Lines with no amount, so missing from `premium`; all of them means it is unknown. */
  unpricedLines: number
}

export interface PremiumBreakdownTotals {
  policies: number
  coverageLines: number
  /** Policies selling more than one coverage — what the single-row view collapsed. */
  bundledPolicies: number
  premium: number
  fees: number
  /** Bound policies with no coverage that carries a premium; fees don't count. */
  policiesWithoutPricedCoverage: number
}

/** Every bound policy of one customer; carriers and lines mix freely. */
export interface ClosedWonAccount {
  orgId: string
  account: string
  repId: string
  repName: string
  carriers: string[]
  firstBoundDate: string
  firstPolicyStart: string | null
  premium: number
  coverageCount: number
  /** Coverage and fee lines, with a policy that has none counted as one unpriced line. */
  lineCount: number
  unpricedLines: number
  policies: ClosedWonBreakdownRow[]
}

/**
 * One month of the filtered page. Stages count leads that first reached them that month;
 * the effort columns are the Activity-by-rep table's, summed for the month.
 */
export interface TrendMonth {
  month: string
  leads: number
  contacted: number
  applicationViewed: number
  applicationCompleted: number
  quoting: number
  quoteSent: number
  closedWon: number
  callsOut: number
  callsMissed: number
  callsIn: number
  emailsOut: number
  smsOut: number
  outreach: number
  /** Null when the month has no applications to divide by. */
  outreachPerApplication: number | null
  callsPerApplication: number | null
}

export interface ActivityRepRow {
  repId: string
  repName: string
  outreach: number
  system: number
  callsOut: number
  callsIn: number
  callsMissed: number
  emailsOut: number
  emailsIn: number
  smsOut: number
  smsIn: number
  notes: number
}

export interface SalesAnalyticsSummary {
  funnel: FunnelStage[]
  statusBreakdown: Record<string, number>
  lostReasons: Record<string, number>
  closedWonByPolicyStart: ClosedWonMonth[]
  closedWonByBoundDate: ClosedWonMonth[]
  closedWonByPolicyStartTotals: ClosedWonTotals
  closedWonByBoundDateTotals: ClosedWonTotals
  closedWonByRep: ClosedWonRepRow[]
  closedWonDetail: ClosedWonBreakdownRow[]
  closedWonByAccount: ClosedWonAccount[]
  premiumByLine: PremiumLineTotal[]
  premiumBreakdownTotals: PremiumBreakdownTotals
  activityByRep: ActivityRepRow[]
  monthlyTrend: TrendMonth[]
  /** The web DOT funnel went live here; inbound is zero before it by definition. */
  inboundTrackingStart: string
  /** Orgs held out of every figure above, listed so the exclusion is auditable. */
  excludedInternalOrgs: string[]
  policiesMissingPolicyStart: number
  applicationsWithoutLead: number
}

export interface SalesAnalyticsFilters {
  start?: string
  end?: string
  rep?: string
  source?: LeadSource
  includeInternal?: boolean
}

function buildParams(filters: SalesAnalyticsFilters): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.start) params.start = filters.start
  if (filters.end) params.end = filters.end
  if (filters.rep) params.rep = filters.rep
  if (filters.source) params.source = filters.source
  if (filters.includeInternal) params.include_internal = 'true'
  return params
}

export function useSalesAnalytics(filters: SalesAnalyticsFilters) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.SALES_ANALYTICS, filters],
    queryFn: () =>
      axios
        .get('/api/v1/insure/internal/sales-analytics/summary', { params: buildParams(filters) })
        .then((response) => toCamelCase(response.data) as SalesAnalyticsSummary),
  })
  return { summary: data, isLoading }
}
